import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/**
 * Sounds an operator has uploaded for terminals to play (RL-416).
 *
 * The `contentHash` is the identity everywhere: it is what
 * `kitchen.orders.newOrderSoundFile` stores and what the terminal fetches by.
 * `originalFilename` exists only so the picker can show a name instead of
 * sixty-four hex characters.
 */
export type TerminalAudioAsset = {
  contentHash: string;
  originalFilename: string;
  contentType: string;
  byteSize: number;
  createdAt: string;
  createdBy: string | null;
};

const KEY = ['terminal-audio'] as const;

export function useTerminalAudio(enabled = true) {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<TerminalAudioAsset[]> => {
      const res = await apiClient.get<TerminalAudioAsset[]>('/terminal-audio');
      return res.data;
    },
    enabled,
  });
}

export function useUploadTerminalAudio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<TerminalAudioAsset> => {
      // Raw body, not multipart — the backend reads the request body directly,
      // the same way item images are uploaded.
      const res = await apiClient.post<TerminalAudioAsset>('/terminal-audio', file, {
        params: { filename: file.name },
        headers: { 'Content-Type': 'audio/wav' },
      });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteTerminalAudio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contentHash: string): Promise<void> => {
      await apiClient.delete(`/terminal-audio/${contentHash}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
